/**
 * usePortCallAggregations - Hook for computing port call statistics
 *
 * Computes aggregations from port call data for both port-centric
 * and vessel-centric views.
 */
import { useMemo } from 'react';
import {
  PortCallData,
  PortCallAggregations,
  PortCallDurationStats,
  DistributionItem,
  TimeSeriesDataPoint,
  AnalyticsMode,
} from './types';

interface UsePortCallAggregationsProps {
  portCalls: PortCallData[];
  mode: AnalyticsMode;
}

/**
 * Calculate duration in seconds between arrival and departure
 */
const calculateDuration = (ata: Date, atd?: Date): number | null => {
  if (!atd) return null;
  return (atd.getTime() - ata.getTime()) / 1000;
};

/**
 * Calculate median from array of numbers
 */
const calculateMedian = (values: number[]): number => {
  if (values.length === 0) return 0;

  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
};

/**
 * Compute duration statistics from port calls
 */
const computeDurationStats = (
  portCalls: PortCallData[],
  mode: AnalyticsMode,
): PortCallDurationStats => {
  const callsWithDuration = portCalls
    .map((call) => ({
      id: call.id,
      label:
        mode === 'port'
          ? call.vesselName || 'Unknown'
          : call.portName || 'Unknown',
      duration: calculateDuration(call.ata, call.atd),
    }))
    .filter(
      (call): call is { id: string; label: string; duration: number } =>
        call.duration !== null,
    );

  if (callsWithDuration.length === 0) {
    return {
      average: 0,
      median: 0,
      min: null,
      max: null,
    };
  }

  const durations = callsWithDuration.map((c) => c.duration);
  const sum = durations.reduce((a, b) => a + b, 0);

  const minCall = callsWithDuration.reduce((min, call) =>
    call.duration < min.duration ? call : min,
  );

  const maxCall = callsWithDuration.reduce((max, call) =>
    call.duration > max.duration ? call : max,
  );

  return {
    average: sum / durations.length,
    median: calculateMedian(durations),
    min: {
      value: minCall.duration,
      portCallId: minCall.id,
      label: minCall.label,
    },
    max: {
      value: maxCall.duration,
      portCallId: maxCall.id,
      label: maxCall.label,
    },
  };
};

/**
 * Compute distribution based on mode
 * - Port mode: Distribution by vessel country (where vessels are from)
 * - Vessel mode: Distribution by port (most visited ports)
 */
const computeDistribution = (
  portCalls: PortCallData[],
  mode: AnalyticsMode,
): DistributionItem[] => {
  const counts = new Map<
    string,
    { label: string; value: string; count: number }
  >();

  portCalls.forEach((call) => {
    let key: string;
    let label: string;
    let value: string;

    if (mode === 'port') {
      // Group by vessel country
      key = call.vesselCountry || 'Unknown';
      label = call.vesselCountry || 'Unknown';
      value = call.vesselCountry || '';
    } else {
      // Group by port
      key = call.portLocode || call.portName || 'Unknown';
      label = call.portName || call.portLocode || 'Unknown';
      value = call.portLocode || '';
    }

    const existing = counts.get(key);
    if (existing) {
      existing.count++;
    } else {
      counts.set(key, { label, value, count: 1 });
    }
  });

  return Array.from(counts.values()).sort((a, b) => b.count - a.count);
};

/**
 * Compute time series data (calls per day)
 */
const computeTimeSeries = (
  portCalls: PortCallData[],
): TimeSeriesDataPoint[] => {
  if (portCalls.length === 0) return [];

  const countsByDay = new Map<string, number>();

  portCalls.forEach((call) => {
    const dateKey = call.ata.toISOString().split('T')[0];
    countsByDay.set(dateKey, (countsByDay.get(dateKey) || 0) + 1);
  });

  return Array.from(countsByDay.entries())
    .map(([dateStr, count]) => ({
      date: new Date(dateStr),
      count,
    }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());
};

/**
 * Main hook for computing port call aggregations
 */
export const usePortCallAggregations = ({
  portCalls,
  mode,
}: UsePortCallAggregationsProps): PortCallAggregations => {
  return useMemo(() => {
    const completedCalls = portCalls.filter((call) => call.atd);

    return {
      totalCalls: portCalls.length,
      completedCalls: completedCalls.length,
      duration: computeDurationStats(portCalls, mode),
      distribution: computeDistribution(portCalls, mode),
      timeSeries: computeTimeSeries(portCalls),
    };
  }, [portCalls, mode]);
};
